import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useAsync} from "react-async-hook";
import {Controller, useForm} from 'react-hook-form';
import ReactModal from "react-modal";
import Switch from "react-switch";
import {vhPresentationApi} from 'apis/admin/presentation/vhPresentationApi';
import ReactSelect from 'react-select';
import {MenuSetMenu} from "../../../../types/admin/menuSet";
import {getMenuTypeSelectOptions, MenuType} from "../../../../enum/menu/MenuType";
import {SelectMenuMaterialModal} from "../MenuMaterial/SelectMenuMaterialModal";
import {MenuMaterial} from "../../../../types/admin/menuMaterial";
import {SelectLabelModal} from "../Label/SelectLabelModal";
import {Label} from "../../../../types/admin/label";
import {VhPresentSelectOptionType} from "../../../../types/admin/vhPresentSelectOptionType";
import useLoading from "../../../../hooks/useLoading";
import useAppRouter from "../../../../hooks/useAppRouter";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import {logError} from "../../../../libs/utils/logError";

interface MenuFormModalProps {
  isEdit: boolean;
  defaultValues: MenuSetMenu;
  labelSetId: number | undefined;
  selectedMenuMaterialIdMap: Map<number, boolean>;
  selectedVhPresentationIdMap: Map<string, boolean>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSave: (menu: MenuSetMenu) => Promise<void>;
  onSaved: () => Promise<void>;
}

interface MenuFormValues {
  name: string;
  enabledFlg: boolean;
  menuTypeId: number;
  menuMaterialId: number | null;
  menuMaterialName: string | null;
  taskCreateFlg: boolean;
  taskCoopLabelId: number | null;
  taskCoopLabelName: string | null;
  vhPresentationId: string | null;
}

export const MenuFormModal = (props: MenuFormModalProps) => {
  const { isEdit, defaultValues, labelSetId, selectedMenuMaterialIdMap, selectedVhPresentationIdMap, setShowModal, onSave, onSaved } = props;
  const [ vhPresentationSelectOptions, setVhPresentationSelectOptions ] = useState<VhPresentSelectOptionType[]>([]);
  const [ showSelectMaterialModal, setShowSelectMaterialModal ] = useState<boolean>(false);
  const [ showSelectLabelModal, setShowSelectLabelModal ] = useState<boolean>(false);
  const selectedMenuMaterialIdMapWithoutEdit = new Map(selectedMenuMaterialIdMap);
  // 編集時は、編集対象が選択していたものは選択可能とする。
  if (isEdit && defaultValues.menuTypeId === MenuType.MATERIAL && defaultValues.menuMaterialId !== null) {
    selectedMenuMaterialIdMapWithoutEdit.delete(defaultValues.menuMaterialId);
  }
  const selectedVhPresentationIdMapWithoutEdit = new Map(selectedVhPresentationIdMap);
  if (isEdit && defaultValues.menuTypeId === MenuType.VH_PRESENTATION && defaultValues.vhPresentationId !== null) {
    selectedVhPresentationIdMapWithoutEdit.delete(defaultValues.vhPresentationId);
  }

  const { goto } = useAppRouter();
  const withLoading = useLoading();
  const location = useLocation();

  const {
    register,
    watch,
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: defaultValues.name,
      menuTypeId: defaultValues.menuTypeId,
      enabledFlg: defaultValues.enabledFlg,
      menuMaterialId: defaultValues.menuMaterialId,
      menuMaterialName: defaultValues.menuMaterialName,
      taskCreateFlg: defaultValues.taskCreateFlg,
      taskCoopLabelId: defaultValues.taskCoopLabelId,
      taskCoopLabelName: defaultValues.taskCoopLabelName,
      vhPresentationId: defaultValues.vhPresentationId,
    },
    reValidateMode: 'onSubmit',
  });

  const menuTypeId = watch('menuTypeId');
  const menuMaterialId = watch('menuMaterialId');
  const menuMaterialName = watch('menuMaterialName');
  const taskCreateFlg = watch('taskCreateFlg');
  const taskCoopLabelId = watch('taskCoopLabelId');
  const taskCoopLabelName = watch('taskCoopLabelName');

  const [abortController, setAbortController] = React.useState<
    AbortController | undefined
    >();

  useAsync(async () => {
    const fetch = async () => {
      try {
        withLoading(async () => {
          setAbortController(new AbortController());
          const response = await vhPresentationApi.index( { abortSignal: abortController?.signal });
          setAbortController(undefined);
          const newVhPresentSelectOptions = response.vhPresentList
            .filter((vhPresent) => {
              return (!selectedVhPresentationIdMapWithoutEdit.has(vhPresent.id));
            })
            .map((vhPresent) => {
              const materialSelectOption: VhPresentSelectOptionType = {
                value: vhPresent.id,
                label: vhPresent.name
              };
              return materialSelectOption;
            });

          setVhPresentationSelectOptions(newVhPresentSelectOptions);
        });
      } catch (e) {
        await logError(
          location.pathname,
          '自動プレゼン一覧の取得に失敗しました。',
          e
        );

        goto('internalServerError');
      }
    };
    await fetch();
  }, []);

  const onSubmit = async (data: MenuFormValues): Promise<void> => {
    await onSave({
      id: defaultValues.id,
      name: data.name,
      menuTypeId: data.menuTypeId,
      menuMaterialId: menuTypeId === MenuType.MATERIAL ? data.menuMaterialId : null,
      menuMaterialName: data.menuMaterialName,
      vhPresentationId:  menuTypeId === MenuType.VH_PRESENTATION ? data.vhPresentationId : null,
      taskCreateFlg: menuTypeId === MenuType.MATERIAL ? data.taskCreateFlg : false,
      taskCoopLabelId: taskCreateFlg ? data.taskCoopLabelId : null,
      taskCoopLabelName: taskCreateFlg ? data.taskCoopLabelName : null,
      enabledFlg: data.enabledFlg,
      menuOrder: defaultValues.menuOrder,
      subMenuList: menuTypeId === MenuType.SUB_MENU ? defaultValues.subMenuList : [],
    });
    await onSaved();
  };

  const handleSelectMenuMaterial = async (menuMaterial: MenuMaterial) => {
    setValue("menuMaterialId", menuMaterial.id);
    setValue("menuMaterialName", menuMaterial.name);
  };

  const handleSelectLabel = async (label: Label) => {
    setValue("taskCoopLabelId", label.id);
    setValue("taskCoopLabelName", label.name);
  };

  return (
    <>
      {showSelectMaterialModal &&
        <SelectMenuMaterialModal
          {...{
            selectedMenuMaterialIdMap: selectedMenuMaterialIdMapWithoutEdit,
            setShowModal: setShowSelectMaterialModal,
            onSelect: handleSelectMenuMaterial,
            onSelected: async () => { setShowSelectMaterialModal(false) },
          }}
        />
       }
      {showSelectLabelModal &&
        <SelectLabelModal
          {...{
            labelSetId,
            setShowModal: setShowSelectLabelModal,
            onSelect: handleSelectLabel,
            onSelected: async () => { setShowSelectLabelModal(false) },
          }}
        />

      }
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="form-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">{ isEdit ? 'メニュー編集' : 'メニュー作成' }</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
            <form>
              <div className="form-modal-content">
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
                <div className="w-full">
                  { /* 名前 */ }
                  <div className="mb-4">
                    <label htmlFor="menuName">
                      <div className="text-xs font-semibold text-primary mb-1">
                        <span>名前</span>
                        <span className="ml-2 required">必須</span>
                      </div>

                      <input
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...register('name',
                          makeRegisterOptions('名前', [
                            { type: FormValidationType.REQUIRED },
                            { type: FormValidationType.MAX_STRING_LENGTH, value: 100 },
                          ])
                        )}
                        id="name"
                        type="text"
                        className="text-field"
                        maxLength={100}
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.name?.message}
                      </div>
                    </label>
                  </div>

                  { /* 有効 */ }
                  <div className="mb-4">
                    <Controller
                      name="enabledFlg"
                      control={control}
                      /* eslint-disable-next-line react/jsx-boolean-value */
                      defaultValue={true}
                      render={({field: { onChange, value }}) => (
                        <Switch
                          onChange={onChange}
                          checked={value}
                          className="react-switch"
                          width={70}
                          uncheckedIcon={
                            <span
                              className="inline-block align-middle font-bold text-white"
                              style={{
                                fontSize: 15,
                                paddingLeft: 3,
                              }}
                            >
                          無効
                        </span>
                          }
                          checkedIcon={
                            <span
                              className="inline-block align-middle font-bold text-white"
                              style={{
                                fontSize: 15,
                                paddingLeft: 8,
                              }}
                            >
                          有効
                        </span>
                          }
                        />)}
                    />
                  </div>

                  { /* メニュータイプ */ }
                  <div className="mb-4">
                    <label htmlFor="menuType">
                      <div className="text-xs font-semibold text-primary mb-1">
                        <span>メニュータイプ</span>
                        <span className="ml-2 required">必須</span>
                      </div>
                      <Controller
                        name="menuTypeId"
                        control={control}
                        render={({field}) =>
                          (
                            <ReactSelect
                              className="w-full text-xs"
                              menuPlacement="auto"
                              options={getMenuTypeSelectOptions()}
                              value={getMenuTypeSelectOptions().find(option => option.value === field.value)}
                              onChange={async (option) => { await field.onChange(option?.value); }}
                            />
                          )
                        }
                        rules={
                          makeRegisterOptions('メニュータイプ', [
                            { type: FormValidationType.REQUIRED },
                          ])
                        }
                      />
                      <div className="mt-1 text-danger text-xs">
                        {errors.menuTypeId?.message}
                      </div>
                    </label>
                  </div>

                  {
                    menuTypeId === MenuType.MATERIAL && (
                      <>
                        { /* 資料 */ }
                        <div className="mb-4">
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor="menuMaterialId">
                            <div className="text-xs font-semibold text-primary mb-1">
                              <span>資料</span>
                              <span className="ml-2 required">必須</span>
                            </div>
                            <div className="flex items-center pt-2 pl-2">
                              <div className="text-sm">{ menuMaterialName }</div>
                              <div className={ menuMaterialId === null ? '' : 'ml-4' }>
                                <button
                                  className="btn-primary"
                                  type="button"
                                  onClick={() => {
                                    setShowSelectMaterialModal(true);
                                  }}
                                >
                                  選 択
                                </button>
                              </div>
                              <input
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...register('menuMaterialId',
                                  makeRegisterOptions('資料', [
                                    {
                                      type: FormValidationType.REQUIRED_IF,
                                      typeName: 'requiredIfMenuIsMaterial',
                                      conditionFunc: () => {
                                        return getValues('menuTypeId') === MenuType.MATERIAL;
                                      }
                                    },
                                  ])
                                )}
                                type="hidden"
                              />
                              <input
                                /* eslint-disable-next-line react/jsx-props-no-spreading */
                                {...register('menuMaterialName')}
                                type="hidden"
                              />
                            </div>
                            <div className="mt-1 text-danger text-xs">
                              {errors.menuMaterialId?.message}
                            </div>
                          </label>
                        </div>

                        { /* タスク連携作成 */ }
                        <div className="mb-4">
                          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                          <label htmlFor="name">
                            <div className="text-xs font-semibold text-primary mb-2">
                              タスク連携作成
                            </div>
                            <Controller
                              name="taskCreateFlg"
                              control={control}
                              /* eslint-disable-next-line react/jsx-boolean-value */
                              defaultValue={true}
                              render={({field: { onChange, value }}) => (
                                <Switch
                                  onChange={onChange}
                                  checked={value}
                                  className="react-switch"
                                  width={70}
                                  uncheckedIcon={
                                    <span
                                      className="inline-block align-middle font-bold text-white"
                                      style={{
                                        fontSize: 15,
                                        paddingLeft: 3,
                                      }}
                                    >無効</span>
                                  }
                                  checkedIcon={
                                    <span
                                      className="inline-block align-middle font-bold text-white"
                                      style={{
                                        fontSize: 15,
                                        paddingLeft: 8,
                                      }}
                                    >有効</span>
                                  }
                                />)}
                            />
                          </label>
                        </div>

                        {taskCreateFlg &&
                          (
                            /* タスク連携ラベル */
                            <div className="mb-4">
                              { /* eslint-disable-next-line jsx-a11y/label-has-associated-control */ }
                              <label htmlFor="labelSetId">
                                <div className="text-xs font-semibold text-primary">
                                  タスク連携ラベル
                                </div>
                                <div className="flex items-center pt-2 pl-2">
                                  <div className="text-sm">{taskCoopLabelName}</div>
                                  <div className={taskCoopLabelId === null ? '' : 'ml-4'}>
                                    <button
                                      className="btn-primary"
                                      type="button"
                                      onClick={() => {
                                        setShowSelectLabelModal(true);
                                      }}
                                    >
                                      選 択
                                    </button>
                                  </div>
                                  {(taskCoopLabelId !== null) &&
                                    (
                                      <div className="ml-1">
                                        <button
                                          className="btn-primary"
                                          type="button"
                                          onClick={() => {
                                            setValue('taskCoopLabelId', null);
                                            setValue('taskCoopLabelName', null);
                                          }}
                                        >
                                          削 除
                                        </button>
                                      </div>
                                    )
                                  }
                                  <input
                                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                                    {...register('taskCoopLabelId')}
                                    type="hidden"
                                  />
                                  <input
                                    /* eslint-disable-next-line react/jsx-props-no-spreading */
                                    {...register('taskCoopLabelName')}
                                    type="hidden"
                                  />
                                </div>
                              </label>
                            </div>
                          )
                        }
                      </>
                    )
                  }

                  { /* 自動プレゼン */ }
                  {
                    menuTypeId === MenuType.VH_PRESENTATION &&
                    <div className="mb-4">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="vhPresentationId">
                        <div className="text-xs font-semibold text-primary mb-1">
                          <span>自動プレゼン</span>
                          <span className="ml-2 required">必須</span>
                        </div>
                        <Controller
                          name="vhPresentationId"
                          control={control}
                          render={({field}) =>
                            (
                              <ReactSelect
                                className="w-full text-xs"
                                options={vhPresentationSelectOptions}
                                value={vhPresentationSelectOptions.find(option => option.value === field.value)}
                                onChange={option => {
                                  field.onChange(option?.value);
                                }}
                              />
                            )
                          }
                          rules={
                            makeRegisterOptions('自動プレゼン', [
                              {
                                type: FormValidationType.REQUIRED_IF,
                                typeName: 'requiredIfMenuIsVhPresentation',
                                conditionFunc: () => {
                                  return getValues('menuTypeId') === MenuType.VH_PRESENTATION;
                                }
                              },
                            ])
                          }
                        />
                        <div className="mt-1 text-danger text-xs">
                          {errors.vhPresentationId?.message}
                        </div>
                      </label>
                    </div>
                  }
                </div>
              </div>
              <div className="form-modal-footer-container">
                <div className="form-modal-footer">
                  <button
                    className="btn-sub"
                    type="button"
                    onClick={() => setShowModal(false)}
                  >
                    キャンセル
                  </button>
                  <button
                    className="btn-primary"
                    type="button"
                    onClick={handleSubmit(onSubmit)}
                  >
                    保 存
                  </button>
                </div>
            </div>
          </form>
        </div>

      </ReactModal>
    </>
  );
};
